import { zoomIdentity, ZoomTransform } from 'd3-zoom';

import { TraceViewModel, Appearance } from './trace-viewmodel';
import { CaliperViewModel } from './caliper-viewmodel';
import { AnnotationType } from '../models/iannotation';
import { IChannel } from '../models/ichannel';
import { IFilter } from '../models/ifilter';
import { IEcgData } from '../models/iecgdata';
import { ECG_CONSTANTS } from '../utils/ecg-constants';
import { EcgMeasures } from '../models/ecgmeasures';

export interface IChartContext {
    layers: Array<LayerViewModel>;
    activeLayer: LayerViewModel | null;

    transform: ZoomTransform;
    opacity: number;

    readonly leadWidth: number;
    readonly leadHeight: number;

    updateLeadWidth(leadWidth: number): void;
}

export class ChartViewModel implements IChartContext {
    // data members

    private _layers = new Array<LayerViewModel>();
    get layers() {
        return this._layers;
    }

    activeLayer: LayerViewModel | null = null;

    opacity: number = 1;

    private _leadWidth = 0;
    get leadWidth() {
        return this._leadWidth;
    }
    leadHeight = ECG_CONSTANTS.LEAD_HEIGHT;

    transform = zoomIdentity;

    isBusy = false;

    // constructor

    constructor() {
        // This is intentional
    }

    // public functions

    updateLeadWidth(leadWidth: number) {
        this._leadWidth = leadWidth;
    }
}

export class LayerViewModel {
    // data members

    title: string = '';
    active = false;
    selected = false;
    index = 0;
    translate: string = '';
    appearance: Appearance = new Appearance();
    leadCount: number = 0; // used for arrangement

    private _traces = new Array<TraceViewModel>();
    get traces() {
        return this._traces;
    }
    get visibleTraces() {
        return this._traces.filter((trace) => trace.visible);
    }
    private _referenceTraces = new Array<TraceViewModel>();
    get referenceTraces() {
        return this._referenceTraces;
    }
    get visibleReferenceTraces() {
        return this._referenceTraces.filter((trace) => trace.visible);
    }
    get drawableTraces() {
        return this._traces.concat(this._referenceTraces).filter((trace) => trace.visible);
    }

    get patient() {
        return this._ecgData.patient;
    }

    get study() {
        return this._ecgData.study;
    }

    private _filters = new Array<IFilter>();
    get filters() {
        return this._filters.filter((filter) => filter.value > '0');
    }

    private _conclusions: Array<string>;
    get conclusions() {
        return this._conclusions;
    }

    private _ecgData: IEcgData;
    get ecgData() {
        return this._ecgData;
    }

    // To remove
    channels = new Array<IChannel>();

    // constructor

    constructor(ecgData: IEcgData) {
        this._ecgData = ecgData;
        this._conclusions =
            this._ecgData.originalWaveform.annotations != null
                ? this._ecgData.originalWaveform.annotations
                      .filter((annotation) => annotation.type == AnnotationType.UNFORMATTED_TEXT)
                      .map((annotation) => annotation.name)
                : [];
        this._filters =
            this._ecgData.filters != null && this._ecgData.filters.length > 0
                ? this._ecgData.filters
                : this._ecgData.originalWaveform.filters;
    }
}

export class MeasuresViewModel {
    // data members

    selected: boolean = false;

    private _caliper: CaliperViewModel;
    get caliper() {
        return this._caliper;
    }

    measures: EcgMeasures;

    // constructor

    constructor(caliper: CaliperViewModel, measures: EcgMeasures | null = null) {
        this._caliper = caliper;
        this.measures = measures == null ? new EcgMeasures() : measures;
    }
}

export class MeasuresHandle {
    // data members

    private _measuresList = new Array<MeasuresViewModel>();
    get measuresList() {
        return this._measuresList;
    }

    get count(): number {
        return this._measuresList.length;
    }

    // public functions

    select(caliper: CaliperViewModel) {
        let measuresToSelect = this.measuresList.find(
            (measures) => measures.caliper == caliper && measures.caliper.title == caliper.title,
        );
        this.measuresList.forEach((measures) => (measures.selected = false));
        if (measuresToSelect) {
            measuresToSelect.selected = true;
        }
    }

    update(caliper: CaliperViewModel, measures: EcgMeasures) {
        let measuresToUpdate = this.measuresList.find(
            (measures) => measures.caliper == caliper && measures.caliper.title == caliper.title,
        );
        this.measuresList.forEach((measures) => (measures.selected = false));
        if (measuresToUpdate != null && measuresToUpdate != undefined) {
            measuresToUpdate.measures = measures;
            measuresToUpdate.selected = true;
        }
    }

    remove(caliper: CaliperViewModel) {
        let measuresToRemove = this.measuresList.find(
            (measures) => measures.caliper == caliper && measures.caliper.title == caliper.title,
        );
        if (measuresToRemove != null && measuresToRemove != undefined) {
            measuresToRemove.selected = false;
            this.measuresList.splice(this.measuresList.indexOf(measuresToRemove), 1);
        }
    }

    clear() {
        this.measuresList.forEach((measures) => (measures.selected = false));
        this._measuresList = new Array<MeasuresViewModel>();
    }
}

export class MeasuresSummaryViewModel {
    // data members

    visible = false;

    private _measuresHandle = new MeasuresHandle();
    get measuresHandle() {
        return this._measuresHandle;
    }

    // constructor

    constructor() {
        // This is intentional
    }
}
